/**
 * scenario-start-ux (Phase 4, Task 2.2) stage presentation components。
 *
 * 3 stage (title / worldview / closing) の sub-component。
 * 共通 props = `{ scenario, reducedMotion }`、ClosingStage のみ `longRunning` を追加。
 *
 * `reducedMotion=true` のときは fade / translate の transition class を無効化する
 * (`motion-reduce:transition-none` は媒介として tailwind が CSS media query を使うが、
 * JS 検出結果と揃えるため直接 transition class の有無で切替える)。
 */

import { extractWorldview } from "./scenario-intro.types";
import type {
  ClosingStageProps,
  StageProps,
} from "./scenario-intro.types";

const TRANSITION_BASE = "transition-opacity duration-700 ease-out";

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function transitionClass(reducedMotion: boolean): string {
  return reducedMotion ? "" : TRANSITION_BASE;
}

export function TitleStage({ scenario, reducedMotion }: StageProps): JSX.Element {
  return (
    <section
      data-testid="scenario-intro-title"
      className={`flex flex-col items-center gap-2 opacity-100 ${transitionClass(reducedMotion)}`}
    >
      <h1 className="text-3xl md:text-5xl font-bold text-reader-primary text-center">
        {scenario.title}
      </h1>
    </section>
  );
}

export function WorldviewStage({
  scenario,
  reducedMotion,
}: StageProps): JSX.Element | null {
  // worldview は OpenAPI 未 expose の JSON payload。extract helper で defensive に抽出。
  const worldview = extractWorldview(scenario);
  const setting = asString(worldview?.setting);
  const tone = asString(worldview?.tone);
  const premise = asString(worldview?.premise);
  // 全 field 未取得 (fallback) の場合は null (WorldviewStage 自体を非表示)
  if (setting === null && tone === null && premise === null) {
    return null;
  }
  return (
    <section
      data-testid="scenario-intro-worldview"
      className={`flex flex-col items-center gap-2 text-center ${transitionClass(reducedMotion)}`}
    >
      {setting !== null && (
        <p className="text-sm md:text-base text-reader-secondary max-w-xl">
          <span className="text-reader-muted">舞台: </span>
          {setting}
        </p>
      )}
      {tone !== null && (
        <p className="text-sm md:text-base text-reader-secondary max-w-xl">
          <span className="text-reader-muted">トーン: </span>
          {tone}
        </p>
      )}
      {premise !== null && (
        <p className="text-sm md:text-base text-reader-primary max-w-xl italic">
          {premise}
        </p>
      )}
    </section>
  );
}

export function ClosingStage({
  reducedMotion,
  longRunning,
}: ClosingStageProps): JSX.Element {
  return (
    <section
      data-testid="scenario-intro-closing"
      className={`flex flex-col items-center gap-2 ${transitionClass(reducedMotion)}`}
    >
      <p className="text-base md:text-lg text-reader-primary">世界に入る…</p>
      {longRunning && (
        <p
          data-testid="scenario-intro-long-running"
          className="text-xs text-amber-300"
        >
          生成に時間がかかっています…
        </p>
      )}
    </section>
  );
}
